import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Flex, InputNumber, message, Select, Space, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PtoReferralConfigItem } from 'Modules'
import { useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { FEATURE } from 'constants/feature'
import { PTOTypeEnum } from 'constants/pto'
import { usePermission } from 'hooks/use-permission'
import useSearchParams from 'hooks/use-search-params'
import { logError, logInfo } from 'utils/logger'

const REWARD_TOKEN_OPTIONS = [{ value: 1, label: 'MEY' }]

const getTokenLabel = (value: number) =>
  REWARD_TOKEN_OPTIONS.find((opt) => opt.value === value)?.label ?? 'MEY'

const fetchPtoReferralConfigs = async (searchParams: Record<string, any>) => {
  const response = await AppAdmin.getPtoReferralConfigs(searchParams)
  logInfo('PTO referral configs', response.data.data)

  if (response.data?.data) {
    return {
      configs: response.data.data.results as PtoReferralConfigItem[],
      total: response.data.data.totalResults,
    }
  }
  return { configs: [], total: 0 }
}

export const PtoReferralTable = () => {
  const queryClient = useQueryClient()
  const { searchParams, initPagination, onChangePagination } = useSearchParams()
  const { canCRUD } = usePermission()
  const canEdit = canCRUD(FEATURE.PTO_REFERRAL)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingValue, setEditingValue] = useState<number>(0)
  const [editingToken, setEditingToken] = useState<number>(1)
  const [saving, setSaving] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['ptoReferralConfigs', searchParams],
    queryFn: () => fetchPtoReferralConfigs(searchParams),
    gcTime: 30000,
  })

  const configs = data?.configs ?? []
  const total = data?.total ?? 0

  const handleEdit = (record: PtoReferralConfigItem) => {
    setEditingId(record.ptoId)
    setEditingValue(record.referralReward)
    setEditingToken(record.rewardToken)
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditingValue(0)
    setEditingToken(1)
  }

  const handleSave = async (record: PtoReferralConfigItem) => {
    if (editingValue < 0) {
      message.error('Referral reward must be a positive number')
      return
    }

    try {
      setSaving(true)

      await AppAdmin.updatePtoReferralConfig(record.ptoId, {
        referralReward: editingValue,
        rewardToken: editingToken,
      })

      message.success('Referral reward updated successfully')
      setEditingId(null)
      queryClient.invalidateQueries({ queryKey: ['ptoReferralConfigs'] })
    } catch (error) {
      logError('Error updating referral reward:', error)
      message.error('Failed to update referral reward')
    } finally {
      setSaving(false)
    }
  }

  const columns: ColumnsType<PtoReferralConfigItem> = [
    {
      title: 'PTO Name',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 300,
    },
    {
      title: 'PTO Type',
      dataIndex: 'ptoType',
      key: 'ptoType',
      width: 120,
      render: (type: string) => {
        if (type === PTOTypeEnum.RENTAL_PTO) return <Tag color="purple">Rental</Tag>
        return <Tag color="blue">PTO</Tag>
      },
    },
    {
      title: 'PTO Price',
      dataIndex: 'pricePerNFT',
      key: 'pricePerNFT',
      width: 150,
      render: (price: number) => `$${new Intl.NumberFormat('en-US').format(price)}`,
    },
    {
      title: 'Referral Reward',
      dataIndex: 'referralReward',
      key: 'referralReward',
      width: 250,
      render: (_: number, record: PtoReferralConfigItem) => {
        const isEditing = editingId === record.ptoId

        if (isEditing) {
          return (
            <Flex align="center" gap={8}>
              <InputNumber
                value={editingValue}
                onChange={(val) => setEditingValue(val ?? 0)}
                min={0}
                precision={2}
                controls={false}
                autoFocus
                style={{ width: 120 }}
                onPressEnter={() => handleSave(record)}
              />
              <Select
                value={editingToken}
                onChange={setEditingToken}
                options={REWARD_TOKEN_OPTIONS}
                style={{ width: 90 }}
              />
              <Button
                type="text"
                icon={<CheckOutlined />}
                onClick={() => handleSave(record)}
                loading={saving}
                style={{ color: '#52c41a' }}
              />
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleCancel}
                disabled={saving}
                style={{ color: '#ff4d4f' }}
              />
            </Flex>
          )
        }

        const tokenLabel = getTokenLabel(record.rewardToken)
        return (
          <Space>
            <span>
              {new Intl.NumberFormat('en-US').format(record.referralReward)} {tokenLabel}
            </span>
            {canEdit && (
              <Button
                type="text"
                icon={<EditOutlined style={{ color: '#1677ff' }} />}
                onClick={() => handleEdit(record)}
                size="small"
              />
            )}
          </Space>
        )
      },
    },
    {
      title: 'Total Payout',
      dataIndex: 'totalPayout',
      key: 'totalPayout',
      width: 200,
      render: (payout: any[], record: PtoReferralConfigItem) => {
        if (!payout?.length) {
          return `0 ${getTokenLabel(record.rewardToken)}`
        }
        const total = payout.reduce((sum: number, item: any) => sum + (item.amount ?? 0), 0)
        return `${new Intl.NumberFormat('en-US').format(total)} ${getTokenLabel(record.rewardToken)}`
      },
    },
  ]

  return (
    <Table
      columns={columns}
      dataSource={configs}
      rowKey="ptoId"
      loading={isLoading}
      pagination={initPagination(total)}
      onChange={onChangePagination}
      scroll={{ x: 1000 }}
      bordered
    />
  )
}
